import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactTable from 'react-table';
import { Eye } from 'styled-icons/feather/Eye';

import { COLORS, Separator } from '@cuemath/comps';

import Pagination from 'modules/list/pagination';
import DateRangeFilter from '../../comps/date-range-filter';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';
import { getCookie } from '../../utils';

const TableWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  overflow-y: hidden;
`;

const SearchWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY_LIGHT};
  border-radius: 8px;
  padding: 0 12px;
  min-height: 38px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.COLA};
  }

  &:focus-within {
    border-color: ${COLORS.COLA};
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const FilterDropdown = styled.select`
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${COLORS.COLA};
  cursor: pointer;
  padding: 0;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 18px;
  background: ${COLORS.GRAY_LIGHT};
  margin: 0 10px;
  flex-shrink: 0;
`;

const FilterInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  width: 280px;

  &::placeholder {
    color: ${COLORS.GRAY};
  }
`;

const ClickableName = styled.span`
  color: ${COLORS.COLA};
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: ${COLORS.COLA_DARK};
  }
`;

const EyeIcon = styled(Eye)`
  color: #333333;
  cursor: pointer;
  width: 20px;
  height: 20px;
  stroke-width: 2;
  &:hover {
    color: #000000;
  }
`;

const EnrolledBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    if (props.enrolled === 'Yes') return '#d4edda';
    if (props.enrolled && props.enrolled !== '') return '#f8d7da';
    return COLORS.GRAY; // grey for 'Unknown' or blank
  }};
  color: ${props => {
    if (props.enrolled === 'Yes') return '#155724';
    if (props.enrolled && props.enrolled !== '') return '#721c24';
    return 'white'; // white text on grey for 'Unknown'
  }};
`;

const SearchPromptWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: ${COLORS.WHITE};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SearchPromptTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const SearchPromptText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
`;

const SearchPromptHint = styled.div`
  font-size: 14px;
  color: #555;
  text-align: left;
  p {
    margin: 6px 0;
  }
`;

function TrialTable({
  trials,
  onSearch,
  onFilter,
  onPageChange,
  countData,
  loading,
  currentPage,
  filters: actualFilters = {},
  idFilter = {},
  onViewTrial,
}) {
  const [localFilters, setLocalFilters] = useState({
    region: actualFilters.region || '',
    channel: actualFilters.channel || '',
    trialVersion: actualFilters.trialVersion || '',
    grade: actualFilters.grade || '',
    programType: actualFilters.programType || '',
    enrolled: actualFilters.enrolled || '',
    start_date: actualFilters.start_date || null,
    end_date: actualFilters.end_date || null,
  });

  const restoredType = Object.keys(idFilter)[0];
  const restoredValue = restoredType ? idFilter[restoredType] : '';
  const [filterType, setFilterType] = useState(restoredType || 'student_id');
  const [filterValue, setFilterValue] = useState(restoredValue);
  const [searchSubmitted, setSearchSubmitted] = useState(!!restoredValue);
  const lastTrackedDateRange = React.useRef(null);

  const userId = getCookie('user_id') || getCookie('emp_id') || '';

  // Keep local filters in sync with actual filters
  React.useEffect(() => {
    setLocalFilters({
      region: actualFilters.region || '',
      channel: actualFilters.channel || '',
      trialVersion: actualFilters.trialVersion || '',
      grade: actualFilters.grade || '',
      programType: actualFilters.programType || '',
      enrolled: actualFilters.enrolled || '',
      start_date: actualFilters.start_date || null,
      end_date: actualFilters.end_date || null,
    });
  }, [actualFilters]);

  const handleDateRangeChange = dateRange => {
    // Convert Date objects to Unix timestamps (seconds since epoch)
    let newFilters;

    if (dateRange && (dateRange.startDate || dateRange.endDate)) {
      // Only track if the values have actually changed (prevents duplicate events)
      const currentKey = `${dateRange.startDate?.getTime()}_${dateRange.endDate?.getTime()}`;
      if (lastTrackedDateRange.current !== currentKey) {
        trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_DATE_FILTER_APPLIED, {
          has_start_date: !!dateRange.startDate,
          has_end_date: !!dateRange.endDate,
        });
        lastTrackedDateRange.current = currentKey;
      }

      newFilters = {
        ...localFilters,
        start_date: dateRange.startDate
          ? Math.floor(new Date(dateRange.startDate).setHours(0, 0, 0, 0) / 1000)
          : null,
        end_date: dateRange.endDate
          ? Math.floor(new Date(dateRange.endDate).setHours(23, 59, 59, 999) / 1000)
          : null,
      };
      // Remove old date_range field if it exists
      delete newFilters.date_range;
    } else {
      // Clear date filters - reset tracking ref
      lastTrackedDateRange.current = null;
      newFilters = { ...localFilters };
      delete newFilters.start_date;
      delete newFilters.end_date;
      delete newFilters.date_range;
    }

    setLocalFilters(newFilters);
    onFilter(newFilters);
  };

  const tableColumns = [
    {
      Header: `Trials (${countData || trials.length || 0})`,
      columns: [
        {
          Header: 'Actions',
          accessor: 'actions',
          width: 60,
          Cell: row => (
            <div
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <EyeIcon onClick={() => onViewTrial(row.original)} />
            </div>
          ),
        },
        {
          Header: 'Student Name',
          accessor: 'studentName',
          width: 150,
          Cell: row => (
            <ClickableName
              onClick={() => {
                trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_STUDENT_NAME_CLICKED, {
                  student_id: row.original.studentId,
                  user_id: userId,
                });
                window.open(`/students/${row.original.studentId}`, '_blank');
              }}
            >
              {row.original.studentName}
            </ClickableName>
          ),
        },
        {
          Header: 'Tutor Name',
          accessor: 'tutorName',
          width: 150,
          Cell: row => (
            <ClickableName
              onClick={() => {
                trackEvent(EVENT_TYPE.TRIAL_DASHBOARD_TUTOR_NAME_CLICKED, {
                  tutor_id: row.original.tutorId,
                  user_id: userId,
                });
                window.open(`/users/teachers/${row.original.tutorId}`, '_blank');
              }}
            >
              {row.original.tutorName}
            </ClickableName>
          ),
        },
        {
          Header: 'Grade',
          accessor: 'grade',
          width: 80,
          Cell: row => <span style={{ fontWeight: '500' }}>{row.original.grade}</span>,
        },
        {
          Header: 'Subject',
          accessor: 'subject',
          width: 100,
          Cell: row => {
            // Color mapping for program types using the provided palette
            const getSubjectColor = subject => {
              const upperSubject = subject?.toUpperCase();
              switch (upperSubject) {
                case 'MATH':
                  return '#1EB5F0'; // Blue 4
                case 'CODING':
                  return '#3DD771'; // Green 4
                case 'ELA':
                  return '#FFBA07'; // Yellow 4
                case 'SAT':
                  return '#ED6CEF'; // Purple 4
                case 'ELP':
                  return '#FF7E3B'; // Orange 4
                case 'SCIENCE':
                  return '#808080'; // Grey 4
                default:
                  return '#6c757d'; // Default gray for unknown subjects
              }
            };

            const backgroundColor = getSubjectColor(row.original.subject);
            const textColor = backgroundColor === '#FFBA07' ? '#000000' : '#ffffff'; // Black text for yellow, white for others

            return (
              <span
                style={{
                  backgroundColor,
                  color: textColor,
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                {row.original.subject}
              </span>
            );
          },
        },
        {
          Header: 'Trial Date',
          accessor: 'trialDate',
          width: 120,
          Cell: row => <span>{row.original.trialDate}</span>,
        },
        {
          Header: 'Duration',
          accessor: 'duration',
          width: 100,
          Cell: row => <span>{row.original.duration}</span>,
        },
        {
          Header: 'Region',
          accessor: 'region',
          width: 80,
          Cell: row => (
            <span
              style={{
                backgroundColor:
                  row.original.region === 'NAM'
                    ? '#007bff'
                    : row.original.region === 'ISC'
                    ? '#28a745'
                    : row.original.region === 'EMEA'
                    ? '#6f42c1'
                    : row.original.region === 'APAC'
                    ? '#17a2b8'
                    : '#6c757d',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
              }}
            >
              {row.original.region}
            </span>
          ),
        },
        {
          Header: 'Channel',
          accessor: 'channel',
          width: 140,
          Cell: row => (
            <span
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
              }}
            >
              {row.original.channel}
            </span>
          ),
        },
        {
          Header: 'Trial Version',
          accessor: 'trialVersion',
          width: 120,
          Cell: row => (
            <span
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
              }}
            >
              {row.original.trialVersion}
            </span>
          ),
        },
        {
          Header: 'Enrolled',
          accessor: 'enrolled',
          width: 100,
          Cell: row => (
            <EnrolledBadge enrolled={row.original.enrolled}>
              {row.original.enrolled || 'Unknown'}
            </EnrolledBadge>
          ),
        },
      ],
    },
  ];

  return (
    <div>
      <SearchWrapper>
        <SearchInputWrapper>
          <FilterDropdown
            value={filterType}
            onChange={e => {
              const newType = e.target.value;
              setFilterType(newType);
              if (filterValue.trim()) {
                setSearchSubmitted(true);
                onSearch({ type: newType, value: filterValue.trim() });
              }
            }}
          >
            <option value="student_id">Student ID</option>
            <option value="teacher_id">Teacher ID</option>
          </FilterDropdown>
          <FilterDivider />
          <FilterInput
            type="text"
            placeholder="Enter ID and press Enter..."
            value={filterValue}
            onChange={e => setFilterValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearchSubmitted(true);
                onSearch({ type: filterType, value: filterValue.trim() });
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                setFilterValue('');
                setSearchSubmitted(false);
                onSearch({ type: filterType, value: '' });
              }
            }}
          />
        </SearchInputWrapper>
        {searchSubmitted && (
          <DateRangeFilter
            placeholder="Filter by date range..."
            onDateRangeChange={handleDateRangeChange}
            value={
              localFilters.start_date || localFilters.end_date
                ? {
                    startDate: localFilters.start_date
                      ? new Date(localFilters.start_date * 1000)
                      : null,
                    endDate: localFilters.end_date
                      ? new Date(localFilters.end_date * 1000)
                      : null,
                  }
                : null
            }
          />
        )}
      </SearchWrapper>

      {!searchSubmitted && !loading ? (
        <SearchPromptWrapper>
          <SearchPromptTitle>Search for Trial Classrooms</SearchPromptTitle>
          <SearchPromptText>
            To get started, please search using one of the following:
          </SearchPromptText>
          <SearchPromptHint>
            <p>
              <strong>Teacher ID</strong> - Search by teacher's unique identifier
            </p>
            <p>
              <strong>Student ID</strong> - Search by student's unique identifier
            </p>
          </SearchPromptHint>
          <SearchPromptText style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
            This search-first approach helps maintain optimal database performance
          </SearchPromptText>
        </SearchPromptWrapper>
      ) : (
        <TableWrapper>
          <ReactTable
            data={trials}
            className="-striped -highlight"
            manual
            showPageSizeOptions={false}
            filterable={false}
            sortable={false}
            resizable={false}
            defaultPageSize={20}
            loading={loading}
            columns={tableColumns}
            style={{ textAlign: 'center' }}
          />
        </TableWrapper>
      )}

      <Separator transparent height={16} />

      {countData > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            page={currentPage || 1}
            perPage={20}
            count={countData}
            onPageChange={onPageChange}
            shouldShowNewUi={true}
          />
        </div>
      )}
    </div>
  );
}

TrialTable.propTypes = {
  trials: PropTypes.array.isRequired,
  onSearch: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  countData: PropTypes.number,
  loading: PropTypes.bool,
  currentPage: PropTypes.number,
  filters: PropTypes.object,
  onViewTrial: PropTypes.func.isRequired,
};

TrialTable.defaultProps = {
  countData: 0,
  loading: false,
  currentPage: 1,
  filters: {},
};

export default TrialTable;
